import {
  Route,
  Switch,
  useLocation,
  Redirect,
  RouteComponentProps,
} from "react-router-dom";

import { Location } from "history";
import { IonRouterOutlet, IonContent } from "@ionic/react";

// Components
import NavBar from "../../components/NavBar";
import NavBanner from "../../components/NavBanner";

import Outbound from "../../components/outbound";
import OutboundLogs from "../../components/outbound/logs/desktop";
import Home from "../../components/home";
import Crm from "../../components/crm";
import CrmCampaigns from "../../components/crm/campaigns";
import CrmLeads from "../../components/crm/leads";

import Chats from "../../components/chats";
import SettingsPage from "./desktop/Settings";
import Integrations from "../../components/integrations";

import CrawlWebsite from "../../components/ai/website";
import FreeText from "../../components/ai/free-text";
import Files from "../../components/ai/files";
import Providers from "../../components/ai/providers";
import CustomerContext from "../../components/ai/customer";
import AiLayout from "../../components/ai/layout";

// Leads
import Payments from "../../components/payments";

import AccountNewInvitation from "../../components/account/accept-invitation";
import AccountCreation from "../../components/account/create";
import { useAppStore } from "../../store/app";
import AiHome from "../../components/ai/home";
import PlaygroundBehavior from "../../components/ai/playground";
import AiEscalations from "../../components/ai/escalations";
import AiFollowUps from "../../components/ai/follow-ups";
import AiSystemPrompt from "../../components/system-prompt";
import AiChannels from "../../components/ai/channels";
import AiIntegrations from "../../components/ai/integrations";
import ProtectedRoute from "../../guards/role-route-guard";
import OnboardingPanel from "../../components/ai/onboarding/onboarding-panel";

const normalizePathname = (pathname: string) => {
  if (!pathname) return "/";
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
};

const isWelcomePath = (pathname: string) => {
  const normalizedPath = normalizePathname(pathname);
  return (
    normalizedPath === "/welcome" || normalizedPath === "/ai/playground/welcome"
  );
};

function OnboardingGuard({
  isOnboarded,
  welcomeCompleted,
  paymentActive,
  isLoading,
  children,
}: {
  isLoading: boolean;
  isOnboarded: boolean;
  welcomeCompleted: boolean;
  paymentActive: boolean;
  children: React.ReactNode;
}) {
  const location = useLocation();

  const isWelcomeRoute = isWelcomePath(location.pathname);
  const isAiRoute = normalizePathname(location.pathname).startsWith("/ai");

  if (
    !isLoading &&
    isOnboarded &&
    !paymentActive &&
    !welcomeCompleted &&
    !isWelcomeRoute
  ) {
    return <Redirect to="/welcome" />;
  }

  if (
    !isLoading &&
    isOnboarded &&
    (welcomeCompleted || paymentActive) &&
    isWelcomeRoute
  ) {
    return <Redirect to="/ai" />;
  }

  if (!isOnboarded && !isAiRoute) {
    return <Redirect to="/ai" />;
  }

  return <>{children}</>;
}
const DesktopHome: React.FC<ILayoutProps> = (props: ILayoutProps) => {
  const { location, onRequestSettings, switchCompany, refreshUser } = props;
  const settings = useAppStore((state) => state.settings);
  const user = useAppStore((state) => state.user);
  const isOnboarded = settings.onboarded;
  const welcomeCompleted = settings.welcomeCompleted;
  const paymentActive = Boolean(settings.status?.payment?.active);
  const isLoading = settings.loading;
  const isWelcomeRoute = isWelcomePath(location.pathname);
  const isOnboardingTransitioning =
    isOnboarded &&
    (settings.onboarding.loading || settings.onboarding.creatingAgent);
  return (
    <>
      <NavBanner
        settings={settings}
        mode={"desktop"}
        hide={!isOnboarded || isOnboardingTransitioning || isWelcomeRoute}
      />
      <NavBar
        user={user}
        switchCompany={switchCompany}
        settings={settings}
        path={location.pathname}
        display={isOnboarded && !isOnboardingTransitioning && !isWelcomeRoute}
      />

      <IonContent>
        <OnboardingGuard
          isOnboarded={isOnboarded}
          welcomeCompleted={welcomeCompleted}
          paymentActive={paymentActive}
          isLoading={isLoading}
        >
          <IonRouterOutlet>
            <Route
              exact
              path="/welcome"
              render={() => {
                if (settings.loading) return null;
                if (!(user.role === "admin" || user.role === "system")) {
                  return <Redirect to="/home" />;
                }
                if (!isOnboarded) return <Redirect to="/ai" />;
                return (
                  <PlaygroundBehavior
                    isolated={true}
                    onSwitchCompany={switchCompany}
                  />
                );
              }}
            />
            <Route
              exact
              path="/ai/playground/welcome"
              render={() => <Redirect to="/welcome" />}
            />
            <Route exact path="/" render={() => <Home />} />
            <Route
              path="/chats"
              render={(routeProps) => <Chats {...routeProps} mode={"chats"} />}
            />
            <Route
              path="/leads"
              render={(routeProps) => <Chats {...routeProps} mode={"leads"} />}
            />
            <Route
              path="/kanban"
              render={(routeProps) => <Chats {...routeProps} mode={"kanban"} />}
            />
            <ProtectedRoute
              role={user.role}
              exact
              path="/outbound"
              render={() => <Outbound settings={settings} />}
            />
            <ProtectedRoute
              role={user.role}
              exact
              path="/integrations"
              render={(routeProps: RouteComponentProps) => (
                <AiLayout>
                  <AiIntegrations {...routeProps} />
                </AiLayout>
              )}
            />
            <ProtectedRoute
              role={user.role}
              exact
              path="/connect"
              render={(routeProps: RouteComponentProps) => (
                <AiLayout>
                  <AiChannels {...routeProps} />
                </AiLayout>
              )}
            />
            <Route path="/outbound/:id" component={OutboundLogs} />
            <Route exact path="/crm" component={Crm} />
            <Route exact path="/crm/campaigns" component={CrmCampaigns} />
            <Route exact path="/crm/leads" component={CrmLeads} />

            <ProtectedRoute
              role={user.role}
              path="/ai"
              render={() => (
                <AiLayout>
                  <Switch>
                    <Route exact path="/ai">
                      {!isOnboarded || isOnboardingTransitioning ? (
                        <OnboardingPanel />
                      ) : (
                        <AiHome />
                      )}
                    </Route>
                    <Route
                      exact
                      path="/ai/playground"
                      render={() => (
                        <PlaygroundBehavior onSwitchCompany={switchCompany} />
                      )}
                    />
                    <Route exact path="/ai/website" component={CrawlWebsite} />
                    <Route exact path="/ai/text" component={FreeText} />
                    <Route exact path="/ai/files" component={Files} />
                    <Route exact path="/ai/providers" component={Providers} />
                    <Route
                      exact
                      path="/ai/escalations"
                      component={AiEscalations}
                    />
                    <Route
                      exact
                      path="/ai/follow-ups"
                      component={AiFollowUps}
                    />
                    <Route
                      exact
                      path="/ai/system-prompt"
                      component={AiSystemPrompt}
                    />
                    <Route exact path="/ai/channels" component={AiChannels} />
                    <Route
                      exact
                      path="/ai/integrations"
                      component={AiIntegrations}
                    />
                    <Route
                      exact
                      path="/ai/customer"
                      component={CustomerContext}
                    />
                  </Switch>
                </AiLayout>
              )}
            />
            <Route
              exact
              path="/payments/settings"
              render={() => <Payments settings={settings} />}
            />
            <Route
              exact
              path="/payments/stripe"
              render={() => <Payments settings={settings} />}
            />
            <Route
              exact
              path="/payments/booking"
              render={() => <Payments settings={settings} />}
            />
            <Route
              exact
              path="/payments/items"
              render={() => <Payments settings={settings} />}
            />
            <Route
              exact
              path="/payments"
              render={() => <Payments settings={settings} />}
            />

            <Route
              exact
              path="/account/accept-invitation"
              render={(routeProps) => (
                <AccountNewInvitation
                  {...routeProps}
                  refreshUser={refreshUser}
                />
              )}
            />
            <Route
              path="/account/create"
              render={() => (
                <AccountCreation
                  switchCompany={switchCompany}
                  refreshUser={refreshUser}
                />
              )}
            />
            <Route
              path="/settings"
              render={() => (
                <SettingsPage
                  user={user}
                  path={location.pathname}
                  settings={settings}
                  onRequestSettings={onRequestSettings}
                  refreshUser={refreshUser}
                />
              )}
            />
            <Route exact path="/cloudbeds" component={Integrations} />
          </IonRouterOutlet>
        </OnboardingGuard>
      </IonContent>
    </>
  );
};

interface ILayoutProps {
  refreshUser: Function;
  switchCompany: Function;
  location: Location;
  onRequestSettings: Function;
}

export default DesktopHome;
